import { jsPDF } from 'jspdf';
import { clean, projectStem } from './exporters';
import { companyLine } from './company';
import { getLogo } from './license';
import { dirText } from './photoDir';
import { mapShot } from './mapShot';

// photo documentation in 3 steps: 1 overview map (where the report / project is) -> 2 area aerial with photo spots -> 3 every photo with spoken text
const M = 14, W = 210, CW = W - 2 * M;
const when = (ms, lang) => (ms ? new Date(ms).toLocaleString(lang, { dateStyle: 'medium', timeStyle: 'short' }) : '');
const pos = (o) => (o?.lat != null ? `${o.lat.toFixed(6)}, ${o.lon.toFixed(6)}` : '');
const img = (doc, src, x, y, maxW, maxH) => { try { const p = doc.getImageProperties(src), s = Math.min(maxW / p.width, maxH / p.height); doc.addImage(src, 'JPEG', x + (maxW - p.width * s) / 2, y, p.width * s, p.height * s); return p.height * s; } catch { return 0; } };
export const photosOf = (r) => (r.photos || []).filter((p) => p.kind !== 'video' && p.data);

export async function photoReportPdf(project, t, lang) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' }), reps = project.reports || [], isProj = project.mode !== 'single';
  let y = M;
  const head = (title, sub) => {
    const logo = getLogo();
    if (logo) img(doc, logo, W - M - 30, M - 4, 30, 14);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(15, 23, 42); doc.text(clean(title).slice(0, 70), M, M + 4);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(90, 90, 90);
    const cl = companyLine(); if (cl) doc.text(clean(cl).slice(0, 120), M, M + 9);
    if (sub) doc.text(clean(sub).slice(0, 130), M, M + 13.5);
    doc.setDrawColor(200, 200, 200); doc.line(M, M + 16, W - M, M + 16);
    y = M + 22; doc.setTextColor(20, 20, 20);
  };
  const para = (s, size = 9.5, bold = false) => {
    if (!s) return;
    doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(size);
    doc.splitTextToSize(clean(s), CW).forEach((ln) => { if (y > 282) { doc.addPage(); y = M; } doc.text(ln, M, y); y += size * 0.45; });
    y += 1.5;
  };
  const at = (r) => (r.lat != null ? r : project);
  // 1 – overview
  const sector = t[`fxS_${project.docSector}`] || '';
  head(project.title || reps[0]?.title || t.fxTitle, [t.fxTitle, sector, when(project.createdAt || reps[0]?.createdAt, lang)].filter(Boolean).join(' · '));
  const leis = project.docSector === 'leisure', act = leis ? t[`fxA_${project.activity}`] || t.fxS_leisure : '';
  para(isProj ? `${leis ? act : t.fxProject}: ${reps.length} ${leis ? t.fxStops : reps.length === 1 ? t.fxReport : t.fxReports}` : leis ? act : t.fxSingle, 11, true);
  para([project.address, pos(at(reps[0] || project))].filter(Boolean).join(' · '));
  const ov = await mapShot({ src: 'osm', zmax: 15, pins: reps.map((r, i) => ({ ...at(r), label: isProj ? i + 1 : '' })) });
  if (ov) y += img(doc, ov, M, y, CW, 120) + 4;
  if (isProj) reps.forEach((r, i) => para(`${i + 1}. ${r.title || t.fxReport} – ${photosOf(r).length} ${photosOf(r).length === 1 ? t.fxPhoto : t.fxPhotos}${r.address ? ` · ${r.address}` : ''}`, 9));
  if (project.aiSummary) { para(t.fxSummary, 11, true); para(project.aiSummary); }
  for (const [ri, r] of reps.entries()) {
    const ph = photosOf(r);
    // 2 – area of this report
    doc.addPage();
    head(`${isProj ? `${ri + 1}. ` : ''}${r.title || t.fxReport}`, [t.fxArea, r.address, pos(at(r)), r.gnss ? 'GNSS' : ''].filter(Boolean).join(' · '));
    const pins = ph.filter((p) => p.lat != null).map((p) => ({ lat: p.lat, lon: p.lon, dir: p.dir, label: ph.indexOf(p) + 1 }));
    const area = await mapShot({ src: 'sat', zmax: 19, pins: pins.length ? pins : [{ ...at(r), label: '' }] });
    if (area) y += img(doc, area, M, y, CW, 130) + 4;
    if (r.aiSummary) { para(t.fxSummary, 11, true); para(r.aiSummary); }
    if (r.text) para(r.text, 9);
    // 3 – each photo with its text
    for (const [i, p] of ph.entries()) {
      if (y > 165) { doc.addPage(); y = M; }
      para(`${t.fxPhoto} ${i + 1}${p.caption ? ` – ${p.caption}` : ''}`, 10.5, true);
      y += img(doc, p.data, M, y, CW, 85) + 3;
      para([when(p.takenAt || p.createdAt, lang), dirText(t, p), pos(p)].filter(Boolean).join(' · '), 8);
      const spoken = (p.memos || []).map((m) => m.transcript).filter(Boolean).join(' ');
      if (spoken) para(`${t.fxSpoken}: „${spoken}“`, 9.5);
      if (p.aiDesc) para(p.aiDesc, 9.5);
      if (p.aiCat || p.tags?.length) para(`${p.aiCat ? `${t.fxCat}: ${p.aiCat}` : ''}${p.tags?.length ? `   ${t.fxTags}: ${p.tags.join(', ')}` : ''}`, 8.5);
      y += 3;
    }
  }
  return { blob: doc.output('blob'), name: `${projectStem(project)}_Fotodoku.pdf` };
}

// share (mail / messenger) where the phone supports it, else download
export async function sharePdf({ blob, name }, title) {
  const file = new File([blob], name, { type: 'application/pdf' });
  if (navigator.canShare?.({ files: [file] })) { try { await navigator.share({ files: [file], title }); return 'shared'; } catch (e) { if (e?.name === 'AbortError') return 'cancel'; } }
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  return 'saved';
}
